 import { Injectable, signal, computed, inject } from '@angular/core';
import { Observable, Subject, fromEvent } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ThemeService } from './theme.service';
import { ConfigService } from './config.service';

export interface IframeMessage {
    type: string;
    [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class IframeCommunicationService {
  themeService = inject(ThemeService);
  configService = inject(ConfigService);
  private messageSubject = new Subject<IframeMessage>();
  private _editMode = signal(false);

  private _isInIframe = signal<boolean>(this.checkIfInIframe());
  
  // Public readable signals
  public isInIframe = computed(() => this._isInIframe());
    public editMode = computed(() => this._editMode());

  constructor() {
    this.initMessageListener();
  }

  /**
   * Checks if the current application is running inside an iframe
   * @returns boolean indicating if the app is in an iframe
   */
  private checkIfInIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch (e) {
      // If we can't access window.top due to same-origin policy, we are in an iframe
      return true;
    }
  }

  /**
   * Initialize the message event listener for receiving messages from parent window
   */
  private initMessageListener(): void {
    if (this.isInIframe()) {
      fromEvent<MessageEvent>(window, 'message')
        .pipe(
          // Filter out messages that don't have data or aren't valid IframeMessage format
          filter(event => {
            try {
              const message = event.data;
              return message && typeof message === 'object' && 'type' in message;
            } catch (e) {
              return false;
            }
          }),
          map(event => event.data as IframeMessage)
        )
        .subscribe(message => {
            // check if editmode is enabled
            if (message.type === 'EDIT_MODE') {
              this._editMode.set(message.payload);              
            } else if (message.type === 'THEME_UPDATE') {
              // Update theme colors based on the message received
              this.themeService._theme.set(message.payload);
              this.themeService.updateCSSVars(message.payload);
              if(message.payload?.fontFamily) this.themeService.updateFontFamily(message.payload?.fontFamily);
            } else if (message.type === 'THEME_GET') {
              // Send the current theme back to the parent window
              this.sendMessage('THEME', {
                theme: this.themeService.theme(),
                logo: this.configService.configState().store?.logo,
              });
            } else if( message.type === 'LOGO_UPDATE') {
              this.updateStoreLogo(message.payload);
            }else if( message.type === 'FONT_FAMILY_UPDATE') {
              this.updateFontFamily(message.payload);
            }
          this.messageSubject.next(message);
        });
    }
  }

  
  updateStoreLogo({ url, altText }: { url: string; altText?: string }) {
    const currentTheme = this.themeService._theme() ?? {};

    this.themeService._theme.set({
      ...currentTheme,
      logo: url,
      altText: altText ?? currentTheme.altText
    });
  }

  /**
   * Listen for specific message types from the parent window
   * @param messageType The type of message to listen for
   * @returns An Observable that emits the payload when messages of the specified type are received
   */
  public onMessage<T = any>(messageType: string): Observable<T> {
    return this.messageSubject.asObservable().pipe(
      filter(message => message.type === messageType),
      map(message => message.payload)
    );
  }

  /**
   * Listen for all messages from the parent window
   * @returns An Observable that emits all iframe messages
   */
  public onAnyMessage(): Observable<IframeMessage> {
    return this.messageSubject.asObservable();
  }

  updateFontFamily(fontFamily: string) {
    this.themeService.updateFontFamily(fontFamily);
  }

  
  /**
   * Notify the parent window about navigation changes
   * @param route The current route path
   */
  public notifyNavigation(route: string): void {
    this.sendMessage('NAVIGATION_CHANGE', { url: route });
  }
  

  /**
   * Send a message to the parent window
   * @param type Message type identifier
   * @param payload Optional data to send with the message
   */
  public sendMessage(type: string, payload?: any): void {
    if (this.isInIframe()) {
      try {
        window.parent.postMessage({ type, ...payload }, '*');
      } catch (e) {
        console.error('Failed to send message to parent window:', e);
      }
    }
  }


}